import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArchive,
  faArrowRotateRight,
  faCheckDouble,
  faClockRotateLeft,
  faCodeBranch,
  faCopy,
  faEllipsisVertical,
  faFilePen,
  faFilter,
  faPencil,
  faPlus,
  faStar,
  faTrash,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { Button } from '@/components/ui/button';
import { SkeletonPage } from '@/components/skeletons';
import { Pagination } from '@/components/shared/Pagination';
import { toast, swalConfirm, confirmDelete } from '@/lib/swal';
import { cn } from '@/lib/cn';
import { useDebounce } from '@/hooks/use-debounce';
import { useHasPermission } from '@/hooks/use-permissions';
import { ROUTES } from '@/routes/routes';
import type {
  ReportTemplateStatus,
  ReportTemplateSummary,
} from '@/services/report-template.service';
import { useReportTemplateList } from './hooks/useReportTemplates';
import {
  useArchiveReportTemplate,
  useCloneReportTemplate,
  useCreateReportTemplate,
  useCreateReportTemplateRevision,
  useDeleteReportTemplate,
  usePublishReportTemplate,
  useSetDefaultReportTemplate,
} from './hooks/useReportTemplateMutations';
import {
  ReportTemplateDefaultBadge,
  ReportTemplateStatusBadge,
  ReportTemplateSystemBadge,
} from './components/ReportTemplateStatusBadge';

const PAGE_SIZE = 20;

function fmtDate(v: string | null | undefined): string {
  if (!v) return '—';
  return new Date(v).toLocaleDateString();
}

function extractMsg(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as { error?: { message?: string } } | undefined;
    if (body?.error?.message) return body.error.message;
  }
  return fallback;
}

function statusOf(error: unknown): number | null {
  return axios.isAxiosError(error) ? (error.response?.status ?? null) : null;
}

// ── Row ───────────────────────────────────────────────────────────────────────

interface RowProps {
  item: ReportTemplateSummary;
  can: (p: string) => boolean;
}

/**
 * One template row and the actions its CURRENT lifecycle state allows.
 *
 * Every `show` below mirrors a rule the backend already enforces, so the menu
 * never offers an operation the server would reject (§4):
 *
 *   publish       DRAFT only
 *   archive       PUBLISHED, not the active default (O-1), not the system template
 *   set default   PUBLISHED only
 *   new version   PUBLISHED only, not the system template (D-078 §8)
 *   delete        DRAFT only, not the default
 *   clone         any template, including the system one — that is the supported
 *                 way to start a design from the platform baseline
 *
 * These are UX guards, not authorization. The backend refuses each of them
 * independently, and a stale row simply produces the server's own error.
 */
function ReportTemplateRow({ item, can }: RowProps) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const publishMutation = usePublishReportTemplate(item.id);
  const archiveMutation = useArchiveReportTemplate(item.id);
  const setDefaultMutation = useSetDefaultReportTemplate(item.id);
  const revisionMutation = useCreateReportTemplateRevision();
  const cloneMutation = useCloneReportTemplate();
  const deleteMutation = useDeleteReportTemplate();

  const openDesigner = () => navigate(ROUTES.REPORT_TEMPLATE_DESIGNER.replace(':id', item.id));

  const handlePublish = async () => {
    const ok = await swalConfirm({
      title: `Publish '${item.name}' v${item.versionNumber}?`,
      text: 'A published version becomes immutable. It does not become the default automatically.',
    });
    if (!ok) return;
    try {
      await publishMutation.mutateAsync();
      toast.success('Version published');
    } catch (e) {
      toast.error(extractMsg(e, 'Failed to publish'));
    }
  };

  const handleArchive = async () => {
    const ok = await swalConfirm({
      title: `Archive '${item.name}' v${item.versionNumber}?`,
      text: 'An archived version is immutable history and cannot be reopened.',
    });
    if (!ok) return;
    try {
      await archiveMutation.mutateAsync();
      toast.success('Version archived');
    } catch (e) {
      toast.error(extractMsg(e, 'Failed to archive'));
    }
  };

  const handleSetDefault = async () => {
    const ok = await swalConfirm({
      title: `Make v${item.versionNumber} the default?`,
      text: 'New reports will freeze this layout. Reports already issued are unaffected.',
    });
    if (!ok) return;
    try {
      await setDefaultMutation.mutateAsync();
      toast.success('Default template updated');
    } catch (e) {
      toast.error(extractMsg(e, 'Failed to set the default'));
    }
  };

  const handleNewVersion = async () => {
    const ok = await swalConfirm({
      title: 'Create New Version?',
      html: `<p class="text-sm">A new draft (v${item.versionNumber + 1}) will be created from the published v${item.versionNumber}. The published version is left untouched.</p>`,
    });
    if (!ok) return;
    try {
      const created = await revisionMutation.mutateAsync(item.id);
      toast.success(`Draft v${created.versionNumber} created`);
      navigate(ROUTES.REPORT_TEMPLATE_DESIGNER.replace(':id', created.id));
    } catch (e) {
      // A 409 means the family already has an open draft — the server's message
      // names it, so it is surfaced verbatim rather than re-worded.
      toast.error(extractMsg(e, 'Failed to create a new version'));
    }
  };

  const handleClone = async () => {
    const ok = await swalConfirm({
      title: `Clone '${item.name}'?`,
      text: 'A clone starts a NEW template family as its own draft v1.',
    });
    if (!ok) return;
    try {
      const created = await cloneMutation.mutateAsync({ id: item.id, dto: {} });
      toast.success('Template cloned');
      navigate(ROUTES.REPORT_TEMPLATE_DESIGNER.replace(':id', created.id));
    } catch (e) {
      toast.error(extractMsg(e, 'Failed to clone'));
    }
  };

  const handleDelete = async () => {
    const ok = await confirmDelete(`${item.name} v${item.versionNumber}`);
    if (!ok) return;
    try {
      await deleteMutation.mutateAsync(item.id);
      toast.success('Draft deleted');
    } catch (e) {
      toast.error(extractMsg(e, 'Failed to delete'));
    }
  };

  const isDraft = item.status === 'DRAFT';
  const isPublished = item.status === 'PUBLISHED';

  const menuItems = [
    {
      label: isDraft && can('report-template:update') ? 'Open designer' : 'View design',
      icon: isDraft && can('report-template:update') ? faPencil : faFilePen,
      action: openDesigner,
      show: true,
    },
    {
      label: 'Version history',
      icon: faClockRotateLeft,
      action: () => navigate(ROUTES.REPORT_TEMPLATE_VERSIONS.replace(':id', item.id)),
      show: true,
    },
    {
      label: 'Publish',
      icon: faCheckDouble,
      action: () => void handlePublish(),
      show: isDraft && !item.isSystem && can('report-template:publish'),
    },
    {
      label: 'Create new version',
      icon: faCodeBranch,
      action: () => void handleNewVersion(),
      show: isPublished && !item.isSystem && can('report-template:create'),
    },
    {
      label: 'Set as default',
      icon: faStar,
      action: () => void handleSetDefault(),
      show: isPublished && !item.isDefault && can('report-template:set-default'),
    },
    {
      label: 'Clone',
      icon: faCopy,
      action: () => void handleClone(),
      show: can('report-template:clone'),
    },
    {
      label: 'Archive',
      icon: faArchive,
      action: () => void handleArchive(),
      show: isPublished && !item.isDefault && !item.isSystem && can('report-template:archive'),
    },
    {
      label: 'Delete',
      icon: faTrash,
      action: () => void handleDelete(),
      show: isDraft && !item.isSystem && !item.isDefault && can('report-template:delete'),
      danger: true,
    },
  ] as const;

  const actionsMenu = (
    <div className="relative">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted"
        aria-label={`Actions for ${item.name} v${item.versionNumber}`}
        aria-expanded={open}
        data-testid={`template-actions-${item.id}`}
      >
        <FontAwesomeIcon icon={faEllipsisVertical} className="h-4 w-4" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} aria-hidden />
          <div className="absolute right-0 top-9 z-20 min-w-[180px] rounded-xl border border-border bg-card py-1 shadow-lg">
            {menuItems
              .filter((m) => m.show)
              .map((m) => (
                <button
                  key={m.label}
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    m.action();
                  }}
                  className={cn(
                    'flex w-full items-center gap-2.5 px-4 py-2 text-sm transition-colors',
                    'danger' in m && m.danger
                      ? 'text-red-600 hover:bg-red-500/10'
                      : 'text-foreground hover:bg-muted',
                  )}
                  data-testid={`template-action-${item.id}-${m.label.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  <FontAwesomeIcon icon={m.icon} className="h-3.5 w-3.5 shrink-0" />
                  {m.label}
                </button>
              ))}
          </div>
        </>
      )}
    </div>
  );

  const badges = (
    <div className="flex flex-wrap items-center gap-1.5">
      <ReportTemplateStatusBadge status={item.status} />
      {item.isDefault && <ReportTemplateDefaultBadge />}
      {item.isSystem && <ReportTemplateSystemBadge />}
    </div>
  );

  return (
    <>
      {/* Desktop row */}
      <tr
        className="hidden cursor-pointer border-b border-border transition-colors last:border-0 hover:bg-muted/20 md:table-row"
        onClick={openDesigner}
        data-testid={`template-row-${item.id}`}
      >
        <td className="px-4 py-3 font-medium text-foreground">{item.name}</td>
        <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{item.slug}</td>
        <td className="px-4 py-3 text-muted-foreground">v{item.versionNumber}</td>
        <td className="px-4 py-3">{badges}</td>
        <td className="px-4 py-3 text-center text-muted-foreground">
          {item.visibleSectionCount}/{item.sectionCount}
        </td>
        <td className="px-4 py-3 text-muted-foreground">{fmtDate(item.publishedAt)}</td>
        <td className="px-4 py-3 text-muted-foreground">{fmtDate(item.updatedAt)}</td>
        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
          {actionsMenu}
        </td>
      </tr>

      {/* Mobile card */}
      <div
        className="rounded-xl border border-border bg-card p-4 md:hidden"
        onClick={openDesigner}
        data-testid={`template-card-${item.id}`}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="truncate font-medium text-foreground">{item.name}</p>
            <p className="mt-0.5 font-mono text-[10px] text-muted-foreground">{item.slug}</p>
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              {badges}
              <span className="text-xs text-muted-foreground">v{item.versionNumber}</span>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              {item.visibleSectionCount} of {item.sectionCount} sections visible
            </p>
          </div>
          <div onClick={(e) => e.stopPropagation()}>{actionsMenu}</div>
        </div>
      </div>
    </>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

/**
 * Report Templates — the MSE's template library (B6.9e §3).
 *
 * Reached from Reports → Report Templates. Lists the tenant's templates with the
 * lifecycle facts that decide what can be done to each, and hands off to the
 * designer. Follows the Procedure Library's structure so the two template
 * managers read the same way.
 *
 * `tenantId` is neither displayed nor sent: the API does not accept one, and the
 * caller's tenant is implied by the session.
 */
export function ReportTemplateLibraryPage() {
  const can = useHasPermission();
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<ReportTemplateStatus | ''>('');
  const debouncedSearch = useDebounce(search, 300);

  const filters = {
    page,
    pageSize: PAGE_SIZE,
    // The API requires at least 2 characters; sending one would be a 400 for a
    // query the user has not finished typing.
    search: debouncedSearch.length >= 2 ? debouncedSearch : undefined,
    status: statusFilter || undefined,
  };

  const query = useReportTemplateList(filters);
  const createMutation = useCreateReportTemplate();

  const handleCreate = async () => {
    const ok = await swalConfirm({
      title: 'New report template?',
      text: 'A new draft is created carrying the standard section layout, ready to design.',
      confirmText: 'Create draft',
    });
    if (!ok) return;
    try {
      const created = await createMutation.mutateAsync({
        name: `Report Template ${new Date().toLocaleDateString()}`,
      });
      toast.success('Draft template created');
      navigate(ROUTES.REPORT_TEMPLATE_DESIGNER.replace(':id', created.id));
    } catch (e) {
      toast.error(extractMsg(e, 'Failed to create the template'));
    }
  };

  const hasActiveFilters = !!statusFilter || debouncedSearch.length >= 2;

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
    setPage(1);
  };

  if (query.isLoading) return <SkeletonPage />;

  // ── Error / permission-denied ──────────────────────────────────────────────
  if (query.error) {
    const status = statusOf(query.error);
    return (
      <div className="space-y-6" data-testid="template-list-error">
        <h1 className="text-xl font-semibold text-foreground">Report Templates</h1>
        <div className="rounded-xl border border-dashed border-border py-16 text-center">
          <FontAwesomeIcon
            icon={faTriangleExclamation}
            className="mb-3 h-8 w-8 text-muted-foreground/40"
          />
          <p className="text-base font-medium text-foreground">
            {status === 403
              ? 'You do not have permission to view report templates.'
              : 'Could not load report templates.'}
          </p>
          {status !== 403 && (
            <>
              <p className="mt-1 text-sm text-muted-foreground">
                {extractMsg(query.error, 'The server did not return the template list.')}
              </p>
              <Button
                variant="secondary"
                onClick={() => void query.refetch()}
                className="mt-4"
                data-testid="retry-template-list"
              >
                <FontAwesomeIcon icon={faArrowRotateRight} className="mr-2 h-3.5 w-3.5" />
                Retry
              </Button>
            </>
          )}
        </div>
      </div>
    );
  }

  const items = query.data?.data ?? [];
  const total = query.data?.total ?? 0;

  return (
    <div className="space-y-6" data-testid="report-template-library">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Report Templates</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Design the master layout every inspection report is built from
          </p>
        </div>
        {can('report-template:create') && (
          <Button
            onClick={() => void handleCreate()}
            disabled={createMutation.isPending}
            className="min-h-[44px]"
            data-testid="new-template"
          >
            <FontAwesomeIcon icon={faPlus} className="mr-2 h-3.5 w-3.5" />
            New Template
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="text"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          placeholder="Search templates…"
          aria-label="Search report templates"
          className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring sm:w-64"
          data-testid="template-search"
        />
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value as ReportTemplateStatus | '');
            setPage(1);
          }}
          aria-label="Filter by status"
          className="h-9 rounded-lg border border-input bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          data-testid="template-status-filter"
        >
          <option value="">Draft &amp; Published</option>
          <option value="DRAFT">Draft</option>
          <option value="PUBLISHED">Published</option>
          <option value="ARCHIVED">Archived</option>
        </select>
        {hasActiveFilters && (
          <button
            type="button"
            onClick={clearFilters}
            className="flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
          >
            <FontAwesomeIcon icon={faFilter} className="h-3 w-3" />
            Clear filters
          </button>
        )}
      </div>

      {/* Empty: nothing at all */}
      {items.length === 0 && !hasActiveFilters && (
        <div
          className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border py-20 text-center"
          data-testid="template-list-empty"
        >
          <FontAwesomeIcon icon={faFilePen} className="mb-4 h-10 w-10 text-muted-foreground/40" />
          <p className="text-base font-medium text-foreground">No report templates yet</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Create a template, or clone the platform baseline, to start designing your report
            layout.
          </p>
          {can('report-template:create') && (
            <Button
              onClick={() => void handleCreate()}
              disabled={createMutation.isPending}
              className="mt-5 min-h-[44px]"
              data-testid="new-template-empty"
            >
              <FontAwesomeIcon icon={faPlus} className="mr-2 h-3.5 w-3.5" />
              New Template
            </Button>
          )}
        </div>
      )}

      {/* Empty: filter miss */}
      {items.length === 0 && hasActiveFilters && (
        <div
          className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border py-20 text-center"
          data-testid="template-list-filtered-empty"
        >
          <FontAwesomeIcon icon={faFilter} className="mb-4 h-8 w-8 text-muted-foreground/40" />
          <p className="text-base font-medium text-foreground">No templates match your filters</p>
          <button
            type="button"
            onClick={clearFilters}
            className="mt-4 text-sm font-medium text-primary hover:underline"
          >
            Clear all filters
          </button>
        </div>
      )}

      {items.length > 0 && (
        <>
          <div className="hidden overflow-hidden rounded-xl border border-border md:block">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  {['Name', 'Slug', 'Ver', 'Status', 'Sections', 'Published', 'Updated', ''].map(
                    (h) => (
                      <th
                        key={h}
                        className={cn(
                          'px-4 py-3 text-left font-semibold text-foreground',
                          h === 'Sections' && 'text-center',
                        )}
                      >
                        {h}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <ReportTemplateRow key={item.id} item={item} can={can} />
                ))}
              </tbody>
            </table>
          </div>

          <Pagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} />
        </>
      )}
    </div>
  );
}
